import { useEffect, useState } from "react";
import Modal from "../components/Modal";
import { useRequest } from "../hooks/useRequest";
import {
  getApiKeys,
  deleteApiKey,
  createApiKey,
  ApiKey,
} from "../api/apiKeys";
import { formatDate, formatDateTime } from "../utils/format";
import "./ApiKeys.scss";

const ApiKeys = () => {
  useEffect(() => {
    document.title = "API Keys | CloudStore API";
  }, []);

  const { data, loading, error, reload } = useRequest(getApiKeys);
  const keys = data || [];

  // 新增弹窗状态
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [createErr, setCreateErr] = useState<string | null>(null);
  const [createdToken, setCreatedToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // 删除弹窗状态
  const [deleteTarget, setDeleteTarget] = useState<ApiKey | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteErr, setDeleteErr] = useState<string | null>(null);

  const openCreate = () => {
    setName("");
    setCreateErr(null);
    setCreatedToken(null);
    setCopied(false);
    setCreateOpen(true);
  };

  const closeCreate = () => {
    setCreateOpen(false);
    // 若本次创建了 key，关闭后刷新列表
    if (createdToken) reload();
  };

  const handleCreate = async () => {
    const trimmed = name.trim();
    setCreateErr(null);
    if (trimmed.length < 1 || trimmed.length > 32) {
      setCreateErr("Key name must be 1-32 characters.");
      return;
    }
    setCreating(true);
    try {
      const res = await createApiKey(trimmed);
      setCreatedToken(res.token);
    } catch (e) {
      setCreateErr(e instanceof Error ? e.message : "Failed to create key.");
    } finally {
      setCreating(false);
    }
  };

  const copyToken = async () => {
    if (!createdToken) return;
    try {
      await navigator.clipboard.writeText(createdToken);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* 剪贴板不可用时忽略 */
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteErr(null);
    setDeleting(true);
    try {
      await deleteApiKey(deleteTarget.id);
      setDeleteTarget(null);
      reload();
    } catch (e) {
      setDeleteErr(e instanceof Error ? e.message : "Failed to delete key.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="cs-keys">
      <h1 className="cs-keys__title">API Keys</h1>

      <div className="cs-keys__warning">
        ⚠ Keep your API keys secure. Do not share them in public repositories.
      </div>

      <div className="cs-keys__head">
        <span className="cs-keys__subtitle">API Keys</span>
        <button
          type="button"
          className="cs-keys__create"
          onClick={openCreate}
        >
          + Create New API Key
        </button>
      </div>

      {loading && <div className="cs-keys__muted">Loading…</div>}
      {!loading && error && <div className="cs-keys__error">{error}</div>}

      {!loading && !error && (
        <div className="cs-keys__table-wrap">
          <table className="cs-keys__table">
            <thead>
              <tr>
                <th className="cs-keys__th">Name</th>
                <th className="cs-keys__th">Key</th>
                <th className="cs-keys__th">Created</th>
                <th className="cs-keys__th">Status</th>
                <th className="cs-keys__th">Last Used</th>
                <th className="cs-keys__th">Actions</th>
              </tr>
            </thead>
            <tbody>
              {keys.length === 0 ? (
                <tr>
                  <td className="cs-keys__empty" colSpan={6}>
                    No API keys yet.
                  </td>
                </tr>
              ) : (
                keys.map((k) => {
                  const active = k.status.toUpperCase() === "ACTIVE";
                  return (
                    <tr key={k.id}>
                      <td className="cs-keys__td cs-keys__name">{k.name}</td>
                      <td className="cs-keys__td cs-keys__key">{k.key}</td>
                      <td className="cs-keys__td">{formatDate(k.createdAt)}</td>
                      <td className="cs-keys__td">
                        <span
                          className={`cs-keys__status ${
                            active
                              ? "cs-keys__status--active"
                              : "cs-keys__status--inactive"
                          }`}
                        >
                          {active ? "Active" : k.status}
                        </span>
                      </td>
                      <td className="cs-keys__td">
                        {k.lastUsedAt ? formatDateTime(k.lastUsedAt) : "Never"}
                      </td>
                      <td className="cs-keys__td">
                        <button
                          type="button"
                          className="cs-keys__delete"
                          onClick={() => {
                            setDeleteErr(null);
                            setDeleteTarget(k);
                          }}
                        >
                          🗑 Delete
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* 新增 key 弹窗 */}
      <Modal open={createOpen} onClose={closeCreate}>
        <h2 className="cs-key-modal__title">Create API Key</h2>
        <div className="cs-key-modal__label">
          Key Name (1-32 characters, must be unique)
        </div>
        <input
          className="cs-key-modal__input"
          placeholder="e.g. Production, Development"
          value={name}
          disabled={!!createdToken || creating}
          maxLength={32}
          onChange={(e) => setName(e.target.value)}
        />

        {createErr && <p className="cs-key-modal__err">{createErr}</p>}

        {createdToken && (
          <>
            <p className="cs-key-modal__warn">
              ⚠ Copy and save this key now. It will not be shown again.
            </p>
            <div
              className="cs-key-modal__key-box"
              title="Click to copy"
              onClick={copyToken}
            >
              {createdToken}
            </div>
            {copied && <span className="cs-key-modal__copied">Copied!</span>}
          </>
        )}

        <div className="cs-key-modal__actions">
          <button
            type="button"
            className="cs-key-modal__cancel"
            onClick={closeCreate}
          >
            {createdToken ? "Close" : "Cancel"}
          </button>
          {!createdToken && (
            <button
              type="button"
              className="cs-key-modal__primary"
              disabled={creating}
              onClick={handleCreate}
            >
              {creating ? "Creating…" : "Create Key"}
            </button>
          )}
        </div>
      </Modal>

      {/* 删除 key 弹窗 */}
      <Modal open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <h2 className="cs-key-modal__title cs-key-modal__title--danger">
          Delete API Key
        </h2>
        <p className="cs-key-modal__confirm">
          Are you sure you want to delete the key "{deleteTarget?.name}"?
        </p>
        <div className="cs-key-modal__danger-box">⚠ This cannot be undone.</div>
        {deleteErr && <p className="cs-key-modal__err">{deleteErr}</p>}
        <div className="cs-key-modal__actions">
          <button
            type="button"
            className="cs-key-modal__cancel"
            onClick={() => setDeleteTarget(null)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="cs-key-modal__delete-btn"
            disabled={deleting}
            onClick={confirmDelete}
          >
            {deleting ? "Deleting…" : "Delete"}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default ApiKeys;
